/**
 * SignatureProfilesEditor — Verwaltungs-UI fuer Signatur-Profile (Phase 07 Plan 03).
 *
 * Layout: 2-Spalten
 *   Links (w-60):  Liste aller Profile + "+ Neue Signatur"-Button
 *   Rechts (flex-1): Name-Feld + TipTap-Editor + Speichern-Button
 *
 * Regeln:
 *   - Aktives Profil: gelber Hintergrund (#E9FF57 / #1A1A1A) — NIE weisse Schrift auf Gelb
 *   - Default-Profil: Stern-Icon im Listeneintrag
 *   - Loeschen: window.confirm() mit Hinweis wenn Default-Profil
 *   - Soft-Warn ab 10 Profilen
 *   - D-10: TipTapEditor mit enableImage wiederverwenden — KEIN neuer Editor
 */
import { useEffect, useState, useCallback } from 'react';
import { TipTapEditor } from '../editor/TipTapEditor';
import { sanitizeEditorHtml } from '../editor/sanitizeEditorHtml';
import { SignatureListItem } from './SignatureListItem';
import { leadsApi } from '../../services/leadsApi';
import type { SignatureProfile } from '../../types';

// ── Pure Hilfsfunktionen (exportiert fuer Unit-Tests) ──────────────────────

/**
 * Gibt den Bestaetigungs-Text fuer das Loeschen eines Profils zurueck.
 * Exportiert fuer Unit-Tests.
 */
export function buildDeleteConfirmText(profile: SignatureProfile): string {
  if (profile.isDefault) {
    return (
      `Profil "${profile.name}" wirklich loeschen?\n\n` +
      'Das Default-Profil wird geloescht. Das aelteste verbleibende Profil wird automatisch zum neuen Default.'
    );
  }
  return `Profil "${profile.name}" wirklich loeschen?`;
}

/**
 * Prueft ob die Anzahl der Profile das Soft-Warn-Limit ueberschreitet.
 * Exportiert fuer Unit-Tests.
 */
export function isOverProfileLimit(count: number, limit = 10): boolean {
  return count >= limit;
}

/**
 * Validiert den Profil-Namen (1–100 Zeichen).
 * Exportiert fuer Unit-Tests.
 */
export function validateProfileName(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) return 'Name darf nicht leer sein.';
  if (trimmed.length > 100) return 'Name darf maximal 100 Zeichen lang sein.';
  return null;
}

/**
 * Findet das Default-Profil in einer Liste.
 * Exportiert fuer Unit-Tests.
 */
export function findDefaultProfile(profiles: SignatureProfile[]): SignatureProfile | undefined {
  return profiles.find((p) => p.isDefault);
}

/**
 * Waehlt das naechste Profil nach dem Loeschen aus:
 * das erste in der verbleibenden Liste (oder undefined wenn leer).
 * Exportiert fuer Unit-Tests.
 */
export function pickNextProfile(
  profiles: SignatureProfile[],
  deletedId: number,
): SignatureProfile | undefined {
  const remaining = profiles.filter((p) => p.id !== deletedId);
  return remaining[0];
}

// ── Hauptkomponente ────────────────────────────────────────────────────────

export default function SignatureProfilesEditor() {
  const [profiles, setProfiles] = useState<SignatureProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const selectedProfile = profiles.find((p) => p.id === selectedId) ?? null;

  const loadProfiles = useCallback(async () => {
    try {
      const list = await leadsApi.signatureProfiles.list();
      setProfiles(list);
      return list;
    } catch (err: any) {
      setError('Profile konnten nicht geladen werden.');
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    leadsApi.signatureProfiles.list()
      .then((list) => {
        if (cancelled) return;
        setProfiles(list);
        setLoading(false);
        // Default-Profil automatisch vorauswaehlen
        const def = list.find((p) => p.isDefault) ?? list[0];
        if (def) {
          setSelectedId(def.id);
          setNameInput(def.name);
          setBodyHtml(def.signatureHtml);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Profile konnten nicht geladen werden.');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  function selectProfile(profile: SignatureProfile) {
    setSelectedId(profile.id);
    setNameInput(profile.name);
    setBodyHtml(profile.signatureHtml);
    setSaveError(null);
    setSavedAt(null);
  }

  async function handleAddNew() {
    setSaveError(null);
    try {
      const created = await leadsApi.signatureProfiles.create({
        name: 'Neue Signatur',
        signatureHtml: '',
        isDefault: false,
      });
      const updated = await loadProfiles();
      // Neues Profil direkt anwaehlen
      const fresh = updated.find((p) => p.id === created.id) ?? created;
      selectProfile(fresh);
    } catch (err: any) {
      setSaveError(err?.message ?? 'Neues Profil konnte nicht angelegt werden.');
    }
  }

  async function handleSave() {
    if (!selectedProfile) return;
    const nameError = validateProfileName(nameInput);
    if (nameError) { setSaveError(nameError); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const sanitized = sanitizeEditorHtml(bodyHtml);
      await leadsApi.signatureProfiles.update(selectedProfile.id, {
        name: nameInput.trim(),
        signatureHtml: sanitized,
      });
      setSavedAt(new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }));
      // Liste aktualisieren damit Name-Aenderung in Sidebar sichtbar wird
      const updated = await loadProfiles();
      const fresh = updated.find((p) => p.id === selectedProfile.id);
      if (fresh) setSelectedId(fresh.id);
    } catch (err: any) {
      setSaveError(err?.message ?? 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSetDefault(profile: SignatureProfile) {
    try {
      await leadsApi.signatureProfiles.setDefault(profile.id);
      await loadProfiles();
    } catch (err: any) {
      setSaveError(err?.message ?? 'Default setzen fehlgeschlagen.');
    }
  }

  async function handleDelete(profile: SignatureProfile) {
    const confirmed = window.confirm(buildDeleteConfirmText(profile));
    if (!confirmed) return;
    try {
      await leadsApi.signatureProfiles.remove(profile.id);
      const updated = await loadProfiles();
      // Naechstes Profil anwaehlen oder leeren
      const next = pickNextProfile(profiles, profile.id);
      const nextFresh = next ? updated.find((p) => p.id === next.id) : undefined;
      if (nextFresh) {
        selectProfile(nextFresh);
      } else {
        setSelectedId(null);
        setNameInput('');
        setBodyHtml('');
      }
    } catch (err: any) {
      setSaveError(err?.message ?? 'Loeschen fehlgeschlagen.');
    }
  }

  if (loading) {
    return <p className="text-sm text-gray-500">Lade Signatur-Profile…</p>;
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  const showLimitWarn = isOverProfileLimit(profiles.length);

  return (
    <div className="flex gap-4 min-h-[400px]">
      {/* ── Linke Spalte: Profil-Liste ──────────────────────────────── */}
      <div className="w-60 shrink-0 flex flex-col gap-2">
        <button
          type="button"
          onClick={handleAddNew}
          className="w-full px-3 py-2 text-sm font-semibold rounded bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] transition-colors"
        >
          + Neue Signatur
        </button>
        {showLimitWarn && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Sehr viele Profile — Uebersicht leidet (empfohlen: max. 10).
          </p>
        )}
        <div className="flex flex-col gap-0.5 overflow-y-auto">
          {profiles.map((profile) => (
            <SignatureListItem
              key={profile.id}
              profile={profile}
              isActive={profile.id === selectedId}
              onSelect={() => selectProfile(profile)}
              onSetDefault={() => handleSetDefault(profile)}
              onDelete={() => handleDelete(profile)}
            />
          ))}
          {profiles.length === 0 && (
            <p className="text-xs text-gray-400 px-2 py-2">Noch keine Profile vorhanden.</p>
          )}
        </div>
      </div>

      {/* ── Rechte Spalte: Editor ───────────────────────────────────── */}
      <div className="flex-1 flex flex-col gap-3">
        {selectedProfile ? (
          <>
            <div>
              <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Profil-Name</label>
              <input
                type="text"
                value={nameInput}
                onChange={(e) => { setNameInput(e.target.value); setSavedAt(null); }}
                maxLength={100}
                className="w-full border border-[#DAD9D9] rounded px-3 py-1.5 text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]"
                placeholder="z.B. Standard, Kurz, Urlaub…"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Signatur-Inhalt</label>
              <TipTapEditor
                key={selectedProfile.id}
                content={bodyHtml}
                onChange={setBodyHtml}
                placeholder="Deine Signatur (Name, Position, Telefon)…"
                enableImage
              />
            </div>
            {saveError && <p className="text-sm text-red-600">{saveError}</p>}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium rounded bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] transition-colors disabled:opacity-50"
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
              {savedAt && <span className="text-xs text-green-700">Gespeichert um {savedAt}</span>}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-gray-400">
              Waehle ein Profil aus oder lege ein neues an.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
